import { useEffect } from "react";
import { Link, useLocation, useParams } from "react-router";

import { useWideLayout } from "../../layout/shell-layout";
import { AreaLinks } from "../../operations/components/parts";
import { AsyncState } from "../../ui/async-state";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { cn } from "../../ui/cn";
import { PageHeader } from "../../ui/page-header";
import { useDiagnosticsCapabilities } from "../access";
import { Markdown } from "../markdown";
import { incidentRunbooks, runbookPath, runbookSources, titleOf } from "../runbooks";

// The runbook page (CHT-772): the text of `docs/operations/runbooks/*.md`, shown inside the console, so a diagnostic links to the exact
// section. Anyone who may open Operations may read a runbook. A runbook holds commands and checks only: no secret, no customer content.

// The look of the Markdown elements: Newsreader headings, 15/26 text, brass `code`, bullets and numbers in the muted colour.
const markdownLook =
  "[&_code]:font-mono [&_code]:text-[13px] [&_code]:text-brass [&_h2]:m-0 [&_h2]:font-serif [&_h2]:text-[30px] [&_h2]:leading-9 [&_h2]:font-medium [&_h3]:mt-3.5 [&_h3]:mb-0 [&_h3]:scroll-mt-4 [&_h3]:font-serif [&_h3]:text-2xl [&_h3]:leading-[30px] [&_h3]:font-medium [&_h4]:mt-2 [&_h4]:mb-0 [&_h4]:scroll-mt-4 [&_h4]:text-base [&_h4]:font-semibold [&_li]:my-1 [&_li]:text-[15px] [&_li]:leading-[26px] [&_li]:marker:text-muted-foreground [&_ol]:m-0 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:m-0 [&_p]:text-[15px] [&_p]:leading-[26px] [&_pre]:m-0 [&_pre]:overflow-x-auto [&_pre]:rounded-sm [&_pre]:bg-muted [&_pre]:p-3 [&_pre_code]:text-foreground [&_ul]:m-0 [&_ul]:list-disc [&_ul]:pl-6";

export function RunbookPage() {
  useWideLayout();
  const { slug = "index" } = useParams();
  const { hash } = useLocation();
  const { capabilities, isLoading } = useDiagnosticsCapabilities();
  const source = runbookSources[slug];

  // A link with `#section` lands on the heading with that anchor.
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll again when the address (slug or hash) changes
  useEffect(() => {
    if (hash === "") return;
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView?.();
  }, [hash, slug]);

  if (isLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.read) {
    return (
      <AsyncState
        kind="error"
        title="Not available for your role"
        message="Your staff role does not include the runbooks."
      />
    );
  }
  if (source === undefined) {
    return (
      <AsyncState
        kind="error"
        title="No such runbook"
        message="This runbook does not exist. Open the list of runbooks."
      />
    );
  }

  return (
    <section className="grid grid-cols-[minmax(0,1fr)] gap-6" aria-labelledby="page-title">
      <PageHeader
        eyebrow="Lafayette operations"
        title={slug === "index" ? "Runbooks" : titleOf(slug)}
        titleId="page-title"
        actions={
          <AreaLinks
            links={[
              { to: "/operations/diagnostics", label: "Diagnostics" },
              { to: "/operations", label: "Jobs and queues" },
              { to: runbookPath({ slug: "index", section: null }), label: "All runbooks" },
            ]}
          />
        }
      />
      {/* The text of a runbook is written Markdown: the elements get their look here, from the card that holds them. The text card and the
          list of the other runbooks stand side by side when their container is wide enough, and stack under it. */}
      <div className="@container">
        <div className="flex flex-col gap-5 @[60rem]:flex-row @[60rem]:items-start">
          <Card className={cn("runbook min-w-0 flex-1 p-5 @[60rem]:p-7", markdownLook)}>
            <article aria-labelledby="page-title" className="grid gap-3">
              <Markdown source={source} />
            </article>
          </Card>
          <nav aria-label="Runbooks" className="@[60rem]:w-[340px] @[60rem]:shrink-0">
            <Card>
              <CardHeader className="pb-2.5">
                <CardTitle>Other runbooks</CardTitle>
              </CardHeader>
              <CardContent className="gap-0">
                <ul className="m-0 grid list-none gap-1.5 p-0">
                  {incidentRunbooks
                    .filter((entry) => entry !== slug)
                    .map((entry) => (
                      <li key={entry}>
                        <Link
                          className="inline-flex min-h-11 items-center text-[15px] text-link underline underline-offset-4 hover:no-underline compact:min-h-8"
                          to={runbookPath({ slug: entry, section: null })}
                        >
                          {titleOf(entry)}
                        </Link>
                      </li>
                    ))}
                </ul>
              </CardContent>
            </Card>
          </nav>
        </div>
      </div>
    </section>
  );
}
